// import React, { useEffect, useState } from "react";
import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { AnimatePresence, motion } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import "./pages/AnimationsAndStyles.css";
import "./App.css";
import "./styles/search-filters.css";

import Admin from "./pages/Admin";
import Dashboard from "./pages/Dashboard";
import HeadcountDashboard from "./pages/HeadcountDashboard";
import CandidatesList from "./pages/CandidatesList";
import CandidateDetail from "./pages/CandidateDetail";
import CandidateFeedback from "./pages/feedback";
import InterviewsList from "./pages/InterviewsList";
import OffersList from "./pages/OffersList";
import AIAssistant from "./pages/AIAssistant";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Profile from "./pages/Profile";
import EmployeesList from "./pages/EmployeesList";
import EmployeeDetail from "./pages/EmployeeDetail";
import Login from "./pages/Login";
import OTP from "./pages/OTP";
import Setup2FA from "./pages/Setup2FA";
import CreateJob from "./pages/CreateJob";
import MicrosoftAuthCallback from "./pages/MicrosoftAuthCallback";
import PayrollTracker from "./pages/payrolltracker";
import VariableTracker from "./pages/Variabletracker";
import ProjectFinancialMIS from "./pages/ProjectFinancialMIS";
import CustomersTab from "./pages/CustomersTab";
import ProjectsTab from "./pages/ProjectsTab";
import MastersTab from "./pages/MastersTab";
import Sales, { CreateSales, EditSales } from "./pages/sales";
import SupportTickets from "./pages/SupportTickets";
import ExpenseReimbursement from"./pages/ExpenseReimbursement";
import Home from "./pages/Home";
import ProtectedRoute from "./components/ProtectedRoute";
import NavIcon from "./components/NavIcon";
import ErrorBoundary from "./components/ErrorBoundary";
import Chatbot from "./components/Chatbot";
import NotificationBell from "./components/NotificationBell";
import SessionTimer from "./components/SessionTimer";
import WorkflowMasterPage from "./pages/WorkflowMaster/WorkflowMasterPage";
import Stage1 from "./pages/shareoffer/stage1";
import DelegationManagement from "./pages/DelegationManagement";
import OfferDocuments from "./pages/OfferDocuments";
import { authAPI } from "./api/client";
import { getHomePathForRole, hasPermission, hasRole, isPrivilegedRole, normalizeRole } from "./utils/accessControl";
import { ACTIVE_PAGES, DIVISIONS } from "./constants/pageMaster";
import {
  loadUserAccess,
  clearUserAccess,
  canAccessNavItem,
  canAccessRouteByUser,
  mayViewPage,
} from "./utils/userAccess";
import { recordModuleVisit } from "./utils/recentlyUsed";
import {
  analyticsPageForPath,
  installInteractionTracking,
  setCurrentAnalyticsPage,
  trackAnalyticsEvent,
} from "./utils/analytics";

const LAST_APP_PATH_KEY = "hrportal_last_path";

function getNavItems(role) {
  const items = [];

  items.push(
    { to: "/headcount", label: "HC Dashboard", icon: "chart", permission: "headcount.view" },
    { to: "/employees", label: "Employees", icon: "people", permission: "employee.view" },
    { to: "/payrolltracker", label: "Revenue Tracker", icon: "file", permission: "payroll.view" },
    { to: "/variabletracker", label: "Variable Tracker", icon: "chart", permission: "variable.view" },
    { to: "/project-mis", label: "Project & Financial MIS", icon: "folder", permission: "resource_cost.view" },
    { to: "/customers", label: "Customers", icon: "folder", permission: "customer.view" },
    { to: "/projects", label: "Projects", icon: "folder", permission: "project.view" },
    { to: "/dashboard", label: "TA Dashboard", icon: "home" },
    { to: "/candidates", label: "Candidates", icon: "users" },
    { to: "/jobs", label: "Jobs", icon: "briefcase" },
    { to: "/interviews", label: "Interviews", icon: "calendar" },
    { to: "/ai", label: "AI Assistant", icon: "sparkles" },
    { to: "/offers", label: "Offers", icon: "file", permission: "offer.view" },
    { to: "/delegations", label: "Delegations", icon: "people", permission: "offer.view" },
    { to: "/offer-documents", label: "Document Vault", icon: "folder", permission: "offer.documents" },
    { to: "/masters", label: "Masters", icon: "folder", permission: "master.view" },
    { to:"/sales",label:"Sales",icon:"chart",permission:"sales.view"},
    { to: "/support-tickets", label: "Support Tickets", icon: "support" },
    { to: "/reimbursement", label: "Reimbursement", icon: "receipt", permission: "reimbursement.view" },
    // { to: "/profile", label: "Profile", icon: "user" },
  );

  return items;
}

// Route -> team division lookup derived from the shared Page Master, so the
// sidebar always groups pages by the same team buckets used in Manage Access.
const ROUTE_DIVISION = Object.fromEntries(
  ACTIVE_PAGES.map((p) => [p.route, p.division])
);

// Team division of the route currently in view. While working inside a team the
// Sidebar shows ONLY that team's tabs (plus Home); Home itself renders without
// a Sidebar, so the team to scope to is read from the route the user is on.
function routeDivision(pathname) {
  // Strip query/hash and any trailing slash before matching.
  const path = String(pathname || "").split(/[?#]/)[0].replace(/\/+$/, "") || "/";

  // Deep routes must be matched on the FULL path. `base` below collapses
  // "/admin/workflow-master" to "/admin", so comparing `base` here never matched.
  if (path === "/admin/workflow-master") {
    // This page lives under "Admin" by URL, but it is the Offer Workflow
    // Management sub-tab of the Offers page, which is HR-team owned.
    // Show the HR team's tabs.
    return "HR Team";
  }

  const base = "/" + path.split("/")[1];

  return ROUTE_DIVISION[base] || null;
}

// A user's preferred team division, derived from their role's preferred
// workspace path. This only orders the access-filtered sidebar after login;
// Home itself is the default landing page. Admin-type users prefer /headcount
// (HR Team), and TA roles prefer /dashboard (TA Team). Analytics is no longer
// a portal page (it lives under Sudo Access), so it is never a home division.
function getHomeDivision(role) {
  const homePath = getHomePathForRole(role);
  return ROUTE_DIVISION[homePath] || DIVISIONS[0] || "General";
}

// Group the (already access-filtered) nav items by team division. The user's
// own team is always rendered first, then the remaining teams in Page Master
// order (TA Team, HR Team, Finance Team, Sales Team, Admin Team, Sudo Access).
// Items without a known division fall back to "General". Sudo Access pages
// (e.g. /admin/analytics) never appear here: they render inside the Sudo
// dashboard, not the portal sidebar.
function getNavGroups(role) {
  const groups = new Map();
  for (const div of DIVISIONS) groups.set(div, []);

  for (const item of getNavItems(role)) {
    // Masters is a hard role-locked page. It is visible only to Super Admin,
    // Developer Head and Developer, regardless of RBAC grants.
    if (item.to === "/masters" && !isPrivilegedRole(role)) continue;
    if (!canAccessNavItem(role, item) || !canAccessRouteByUser(role, item.to)) {
      continue;
    }
    const division = ROUTE_DIVISION[item.to] || "General";
    if (!groups.has(division)) groups.set(division, []);
    groups.get(division).push(item);
  }

  const home = getHomeDivision(role);
  const ordered = [...groups.entries()]
    .filter(([, items]) => items.length > 0)
    .sort(([a], [b]) => {
      if (a === home) return -1;
      if (b === home) return 1;
      return 0;
    });

  return ordered;
}

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

function getTransition(prefersReduced) {
  return prefersReduced ? { duration: 0 } : { type: "tween", ease: "anticipate", duration: 0.2 };
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loggedIn, setLoggedIn] = useState(() => !!localStorage.getItem("token"));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(() => resolveCurrentUser());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!loggedIn) return;

    const pathname = location.pathname || "/";
    const isAuthPath =
      pathname.startsWith("/login") ||
      pathname.startsWith("/otp") ||
      pathname.startsWith("/setup-2fa") ||
      pathname.startsWith("/admin") ||
      pathname.startsWith("/auth/");

    if (!isAuthPath) {
      sessionStorage.setItem(LAST_APP_PATH_KEY, pathname);
    }
  }, [loggedIn, location.pathname]);

  // Record the pages the user actually opens so Home's "Recently Used" list
  // can show lightweight navigation history (localStorage only). Sudo pages
  // (/admin/*) are excluded — they are not portal modules.
  useEffect(() => {
    if (!loggedIn) return;
    const path = location.pathname || "/";
    if (path === "/admin" || path.startsWith("/admin/")) return;
    recordModuleVisit(path);
  }, [loggedIn, location.pathname]);

  useEffect(() => {
    if (!loggedIn) return;

    const navEntries = performance.getEntriesByType?.("navigation");
    const isReload = Array.isArray(navEntries) && navEntries[0]?.type === "reload";

    if (!isReload) return;
    if (location.pathname !== "/") return;

    const savedPath = sessionStorage.getItem(LAST_APP_PATH_KEY);
    if (savedPath && savedPath !== "/") {
      navigate(savedPath, { replace: true });
    }
  }, [loggedIn, location.pathname, navigate]);

  useEffect(() => {
    const syncUser = () => setUser(resolveCurrentUser());
    syncUser();
    window.addEventListener("storage", syncUser);
    return () => window.removeEventListener("storage", syncUser);
  }, [loggedIn]);

  // Fetch the user's current profile from the database on mount and whenever
  // the tab becomes visible again, so that role / status changes made by an
  // admin are reflected in the UI without requiring a logout. This is far
  // more efficient than polling because role changes are infrequent events.
  useEffect(() => {
    if (!loggedIn) return;

    let cancelled = false;

    const refreshUserFromDB = async () => {
      try {
        const res = await authAPI.getMe();
        if (!cancelled && res?.data) {
          setUser((prev) => {
            const next = { ...res.data };
            // Preserve token-decoded fields that /auth/me doesn't return
            if (prev?.username) next.username = prev.username;
            return next;
          });
        }
      } catch {
        // /auth/me returns 401 when the token is invalid or the user is
        // disabled. The axios interceptor already handles refresh / logout
        // so a silent catch here is correct.
      }
    };

    refreshUserFromDB();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshUserFromDB();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) {
      clearUserAccess();
      return;
    }

    // (Re)load the matrix snapshot after login AND after every auth change
    // (login event / token refresh / user switch) so the sidebar never renders
    // from the previous user's cached permissions. The reload also re-runs
    // after /auth/me refreshes the role, so a No Role user always ends up on
    // the fresh /my-access response — never a stale snapshot.
    let cancelled = false;
    loadUserAccess().then(() => {
      if (!cancelled) setUser((prev) => ({ ...prev }));
    });
    const onAuth = () => {
      loadUserAccess().then(() => {
        if (!cancelled) setUser((prev) => ({ ...prev }));
      });
    };
    window.addEventListener("hrportal-auth", onAuth);

    return () => {
      cancelled = true;
      window.removeEventListener("hrportal-auth", onAuth);
    };
  }, [loggedIn, user?.id, user?.role]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Records where people went (page_view) and how long they stayed (page_exit,
  // which carries the duration). The exit is emitted from the effect cleanup
  // because leaving a screen and unmounting it are the same event here.
  useEffect(() => {
    if (!loggedIn) return undefined;
    installInteractionTracking();
    const page = analyticsPageForPath(location.pathname);
    if (!page) return undefined;

    setCurrentAnalyticsPage(page);
    const enteredAt = Date.now();
    trackAnalyticsEvent({
      eventType: "page_view",
      pageKey: page.pageKey,
      tabName: page.label,
      featureName: page.label,
      actionName: "view",
      success: true,
      metadata: { route: location.pathname, teamDivision: page.division },
    });

    return () => {
      trackAnalyticsEvent({
        eventType: "page_exit",
        pageKey: page.pageKey,
        tabName: page.label,
        featureName: page.label,
        actionName: "exit",
        success: true,
        durationMs: Date.now() - enteredAt,
        metadata: { route: location.pathname, teamDivision: page.division },
      });
    };
  }, [loggedIn, location.pathname]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    setLoggedIn(false);
    navigate("/login", { replace: true });
  };

  // Home is the personal workspace launcher: it renders WITHOUT a Sidebar.
  // Everywhere else the Sidebar is scoped to the team of the route in view
  // (routeDivision). Routes that belong to no team (e.g. /profile) have no team
  // tabs at all — the Sidebar shows only Home.
  const noSidebar =
    location.pathname === "/home" || location.pathname.startsWith("/home/");
  const currentTeam = routeDivision(location.pathname);

  // Scope nav groups to the current team. No team => no team tabs, Home only.
  const navGroups = currentTeam
    ? getNavGroups(user?.role).filter(([division]) => division === currentTeam)
    : [];

  if (!loggedIn) {
    return (
      <div className="App">
        <ErrorBoundary>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={getTransition(reducedMotion)}
            >
              <Routes>
                <Route path="/login" element={<Login onLoginSuccess={() => { setUser(resolveCurrentUser()); setLoggedIn(true); }} />} />
                <Route path="/setup-2fa" element={<Setup2FA />} />
                <Route path="/otp" element={<OTP onVerified={() => { setUser(resolveCurrentUser()); setLoggedIn(true); }} />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/analytics" element={<Admin initialTab="analytics" />} />
                <Route path="/auth/microsoft" element={<MicrosoftAuthCallback />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </ErrorBoundary>
        <ToastContainer position="bottom-right" autoClose={5000} />
      </div>
    );
  }

  return (
    <>
    <a className="skip-to-content" href="#main-content">Skip to main content</a>
    <div className={`App app-shell ${sidebarOpen ? "" : "sidebar-collapsed"} ${noSidebar ? "no-sidebar" : ""}`.trim()}>
      {!noSidebar && (
      <aside
        className="app-sidebar"
        aria-label="Main navigation"
        onMouseLeave={() => {
          if (window.innerWidth > 980) {
            setSidebarOpen(false);
          }
        }}
      >
        <nav className="sidebar-nav">
          <Link
            to="/home"
            className={`sidebar-link ${location.pathname === "/home" || location.pathname.startsWith("/home/") ? "active" : ""}`}
            aria-current={location.pathname === "/home" || location.pathname.startsWith("/home/") ? "page" : undefined}
          >
            <span className="sidebar-icon" aria-hidden="true"><NavIcon type="home" /></span>
            <span>Home</span>
          </Link>
          {navGroups.map(([division, items]) => {
            return (
              <div key={division} className="sidebar-group">
                <span className="sidebar-group-label">{division}</span>
                {items.map((item) => {
                  const isActive =
                    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`sidebar-link ${isActive ? "active" : ""}`}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className="sidebar-icon" aria-hidden="true"><NavIcon type={item.icon} /></span>
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            );
          })}
          {/* Ensure Workflow Management appears in team scope if relevant */}
        </nav>
      </aside>
      )}

      <div className="app-main">
        <header className="app-topbar">
          <div className="topbar-left">
            {!noSidebar && (
            <button
              type="button"
              className="menu-toggle"
              aria-label="Toggle sidebar"
              onClick={() => setSidebarOpen((prev) => !prev)}
            >
              <span />
              <span />
              <span />
            </button>
            )}
            <Link to="/" className="topbar-logo-link" aria-label="Go to dashboard">
              <img loading="lazy" src={require("./assest/logo.png")} alt="UTS Logo" className="topbar-logo" />
            </Link>
          </div>
          <div className="topbar-right">
            <p className="topbar-date">
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </p>
            <NotificationBell />
            <div className="profile-menu-wrap" ref={profileMenuRef}>
              <button
                type="button"
                className="topbar-user"
                onClick={() => setShowProfileMenu((prev) => !prev)}
                aria-label="Open profile menu"
                aria-expanded={showProfileMenu}
              >
                <span className="topbar-avatar" aria-hidden="true">{(user.name || "U").trim().charAt(0).toUpperCase()}</span>
                <span className={`profile-caret ${showProfileMenu ? "open" : ""}`} aria-hidden="true">▾</span>
              </button>

              {showProfileMenu && (
                <div className="profile-dropdown" role="menu" aria-label="Profile menu">
                  <div className="profile-dropdown-head">
                    <span className="profile-dropdown-avatar" aria-hidden="true">
                      {(user.name || "U").trim().charAt(0).toUpperCase()}
                    </span>
                    <div>
                      <p className="profile-dropdown-name">{user.name}</p>
                      <p className="profile-dropdown-role">{user.role}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="profile-dropdown-item"
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate("/profile");
                    }}
                  >
                    <span className="profile-dropdown-item-icon" aria-hidden="true"></span>
                    My Profile
                  </button>
                  <button
                    type="button"
                    className="profile-dropdown-item danger"
                    onClick={() => {
                      setShowProfileMenu(false);
                      handleLogout();
                    }}
                  >
                    <span className="profile-dropdown-item-icon" aria-hidden="true">↪</span>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <SessionTimer />

        <main id="main-content" className="main-content" tabIndex="-1">
          <ErrorBoundary>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={getTransition(reducedMotion)}
            >
              <Routes>
                <Route path="/" element={<ProtectedRoute><DefaultHome userRole={user?.role} /></ProtectedRoute>} />
                <Route path="/home" element={<ProtectedRoute><Home user={user} userRole={user?.role} /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/dashboard"><Dashboard /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/candidates" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/candidates"><CandidatesList /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/candidates/:id" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/candidates"><CandidateDetail /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/feedback/:id" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/candidates"><CandidateFeedback /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/jobs" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/jobs"><Jobs /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/jobs/create" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/jobs"><CreateJob /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/jobs/:id" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/jobs"><JobDetail /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/interviews" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/interviews"><InterviewsList /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/offers" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/offers"><PermissionGuard userRole={user?.role} permission="offer.view"><OffersList /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/delegations" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/delegations"><DelegationManagement /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/offer-documents" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/offer-documents"><HRDocGuard userRole={user?.role}><OfferDocuments /></HRDocGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/headcount" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/headcount"><PermissionGuard userRole={user?.role} permission="headcount.view"><HeadcountDashboard /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/ai" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/ai"><AIAssistant /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile onLogout={handleLogout} /></ProtectedRoute>} />
                <Route path="/employees" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/employees"><PermissionGuard userRole={user?.role} permission="employee.view"><EmployeesList /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/employees/:id" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/employees"><PermissionGuard userRole={user?.role} permission="employee.view"><EmployeeDetail /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                {/* Trainee and Intern modules are retired: the Zoho headcount
                    feed is the single source of truth. Keep old bookmarks
                    working by redirecting to the Employees page. */}
                <Route path="/trainees" element={<Navigate to="/employees" replace />} />
                <Route path="/trainees/:id" element={<Navigate to="/employees" replace />} />
                <Route path="/interns" element={<Navigate to="/employees" replace />} />
                <Route path="/interns/:id" element={<Navigate to="/employees" replace />} />
                <Route path="/auth/microsoft" element={<MicrosoftAuthCallback />} />
                <Route path="/payrolltracker" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/payrolltracker"><PermissionGuard userRole={user?.role} permission="payroll.view"><PayrollTracker /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/variabletracker" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/variabletracker"><PermissionGuard userRole={user?.role} permission="variable.view"><VariableTracker /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/project-mis" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/project-mis"><PermissionGuard userRole={user?.role} permission="resource_cost.view"><ProjectFinancialMIS /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/customers" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/customers"><PermissionGuard userRole={user?.role} permission="customer.view"><CustomersTab /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/projects" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/projects"><PermissionGuard userRole={user?.role} permission="project.view"><ProjectsTab /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/masters" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/masters"><PrivilegedPageGuard userRole={user?.role}><MastersTab /></PrivilegedPageGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/admin/workflow-master" element={<ProtectedRoute><SuperAdminGuard userRole={user?.role}><WorkflowMasterPage /></SuperAdminGuard></ProtectedRoute>} />
                <Route path="/sales" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/sales"><PermissionGuard userRole={user?.role} permission="sales.view"><Sales userRole={user?.role} /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/sales/new" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/sales"><PermissionGuard userRole={user?.role} permission="sales.create"><CreateSales /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/sales/:id/edit" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/sales"><PermissionGuard userRole={user?.role} permission="sales.edit"><EditSales /></PermissionGuard></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/support-tickets" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/support-tickets"><SupportTickets user={user} /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/reimbursement" element={<ProtectedRoute><AccessRouteGuard userRole={user?.role} pathname="/reimbursement"><ExpenseReimbursement user={user} /></AccessRouteGuard></ProtectedRoute>} />
                <Route path="/shareoffer" element={<ProtectedRoute><Stage1 /></ProtectedRoute>} />
                {/* Retired portal route: Analytics now lives under Sudo Access
                    (/admin/analytics). Keep the bookmark working via redirect;
                    the Sudo login gate there decides who may view it. */}
                <Route path="/analytics" element={<Navigate to="/admin/analytics" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
          </ErrorBoundary>
        </main>
      </div>

      <ToastContainer position="bottom-right" autoClose={5000} />
      <Chatbot />
      <ScrollToTop />
    </div>
    </>
  );
}

function extractNameFromEmail(email) {
  if (!email || typeof email !== "string" || !email.includes("@")) return "";
  return email.split("@")[0];
}

function resolveCurrentUser() {
  try {
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const parsedUser = JSON.parse(rawUser);
      const storedName =
        parsedUser?.full_name ||
        parsedUser?.name ||
        parsedUser?.username ||
        extractNameFromEmail(parsedUser?.email);
      if (storedName) {
        return { name: storedName, role: parsedUser?.role || "HR" };
      }
    }
  } catch {
    // ignore malformed localStorage user
  }

  const token = localStorage.getItem("token");
  if (!token) return { name: "User", role: "HR" };

  try {
    const parsed = jwtDecode(token);
    return {
      name: parsed?.full_name || parsed?.name || extractNameFromEmail(parsed?.email || parsed?.upn) || "User",
      role: parsed?.role || "HR",
    };
  } catch {
    return { name: "User", role: "HR" };
  }
}

function PermissionGuard({ userRole, permission, children }) {
  // Privileged tier (super_admin, Developer Head, Developer) passes every
  // permission gate, matching the backend isFullAccessRole bypass.
  if (isPrivilegedRole(userRole)) return children;
  const permitted = hasPermission(userRole, permission);
  if (permitted) return children;
  // No-role and custom-role users have no legacy permission set. Defer to the
  // snapshot-driven AccessRouteGuard (which resolves employee overrides + role
  // permissions) instead of hard-denying, so matrix-granted access works while
  // the backend still enforces the same matrix.
  if (normalizeRole(userRole) === "unknown") return children;
  // Employees granted access via their user_access row (employee ALLOW) can
  // hold a page the legacy role set never grants (e.g. headcount/employee for
  // the HR role). Defer to the SAME /my-access snapshot resolution used by
  // Home and the sidebar so "Show + Allow" stays consistent everywhere.
  if (permission && mayViewPage(permission.split(".")[0])) return children;
  return <Navigate to="/" replace />;
}

function PrivilegedPageGuard({ userRole, children }) {
  // Hard role lock: no RBAC snapshot, employee ALLOW or custom role can open
  // this page. Keep this list aligned with backend isFullAccessRole().
  if (isPrivilegedRole(userRole)) return children;
  return <Navigate to="/" replace />;
}

function SuperAdminGuard({ userRole, children }) {
  const isSuperAdmin = hasRole(userRole, ["super_admin"]);
  if (!isSuperAdmin) return <Navigate to="/" replace />;
  return children;
}

function HRDocGuard({ userRole, children }) {
  // Use the same effective page VIEW permission as Home/sidebar/backend.
  // Do not maintain a second hard-coded role list here: employee-specific
  // access and custom roles must be able to open Document Vault too.
  if (mayViewPage("offer_doc")) return children;
  if (hasPermission(userRole, "offer.documents")) return children;
  return <Navigate to="/" replace />;
}

// Employee-specific permission-aware route guard used alongside the existing
// role PermissionGuard so that a DENY on a page's VIEW also blocks the route.
// The underlying check (canAccessRouteByUser) is shared with the Home page so
// route gating and Home-card visibility always stay in sync.
function AccessRouteGuard({ userRole, pathname, children }) {
  if (!canAccessRouteByUser(userRole, pathname)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Root "/" landing: every authenticated user is sent to the common Home page.
// Existing dashboard/module routes stay fully reachable via Home, the sidebar
// and direct URLs — only the default landing destination changes.
function DefaultHome() {
  return <Navigate to="/home" replace />;
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
      className={`scroll-to-top ${visible ? "visible" : "hidden"}`}
    >
      ↑
    </button>
  );
}

export default App;
